import { useQuery, UseQueryResult } from "react-query";
import { AxiosError } from "axios";
import { FacetType, IFacet, IFacetResponse } from "shared-services";
import { IOption } from "component-library/src/components/Atoms/FormikInputs/SelectFormik/SelectFormik";

import { QueryKeys } from "../constants/QueryKeys";
import { facetService } from "../services";

export interface IFacetOptions {
  data: IFacetResponse | undefined;
  facetOptions: IOption[];
  loadingFacetOptions: boolean;
  fetchFacetOptions: () => Promise<UseQueryResult<IFacetResponse>>;
  error: unknown;
  formatOptions: (options?: IFacet[]) => IOption[];
}

/**
 * Fetches a list of facet options and returns them in
 * dropdown options format
 */
const useFacetOptions = (
  facetType: FacetType,
  shouldFireQuery: boolean
): IFacetOptions => {
  const { data, isLoading, refetch, error } = useQuery<
    IFacetResponse,
    AxiosError
  >(
    [QueryKeys.FETCH_FACET_OPTIONS, facetType],
    async () => {
      const res = await facetService.getFacets({ category: facetType });

      return res[0];
    },
    {
      cacheTime: Infinity,
      staleTime: Infinity,
      enabled: shouldFireQuery,
    }
  );

  const facetOptions = data?.facetGroups?.[0].facets;

  const formatOptions = (options?: IFacet[]): IOption[] => {
    return (
      options?.map((facet) => ({
        label: facet.name,
        value: facet.id,
      })) || []
    );
  };

  const formattedOptions = formatOptions(facetOptions);

  return {
    data,
    facetOptions: formattedOptions || [],
    loadingFacetOptions: isLoading,
    fetchFacetOptions: refetch,
    error,
    formatOptions,
  };
};

export default useFacetOptions;
